import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../assets/Logo.png";
import "../styles/Footer.css";

function scrollToId(id, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (retries > 0) {
    setTimeout(() => scrollToId(id, retries - 1), 80);
  }
}

/* ─────────────────────────────────────────────
   Contact details — same number/format used across
   the site (Navbar, WhatsApp booking flows, etc.)
───────────────────────────────────────────── */
const WHATSAPP_NUMBER = "917358798182";
const WHATSAPP_MESSAGE =
  "Hi, I'd like to know more about TANBIO Biomed Genomics Laboratory's diagnostic services.";
const EMAIL_ADDRESS = "tanbiomedicalab@gmail.com";
const EMAIL_SUBJECT = "Enquiry - TANBIO Biomed Genomics Laboratory";

function buildWhatsAppUrl(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
}

/* Detects an actual mobile/tablet device (not just a narrow browser
   window) so the Email icon can route correctly — plain mailto:
   works reliably on-device, but frequently does nothing on desktop
   browsers with no mail client configured. */
function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|BlackBerry|webOS/i.test(
    navigator.userAgent
  );
}

const quickLinks = [
  { label: "Home",            route: "/",        scrollId: null          },
  { label: "About Us",        route: "/about",   scrollId: null            },
  { label: "Services",        route: "/service", scrollId: null      },
  { label: "Health Packages", route: "/",        scrollId: "packages"      },
  { label: "Popular Tests",   route: "/",        scrollId: "popular-tests" },
  { label: "Contact Us",      route: "/contact", scrollId: null            },
];

/*
  Schedule — 0=Sun, 1=Mon … 6=Sat
  Mon–Sat : 7:00 AM – 7:00 PM
  Sunday  : 7:00 AM – 1:00 PM
*/
const schedule = {
  0: { day: "Sunday",    open: "07:00", close: "13:00" },
  1: { day: "Monday",    open: "07:00", close: "19:00" },
  2: { day: "Tuesday",   open: "07:00", close: "19:00" },
  3: { day: "Wednesday", open: "07:00", close: "19:00" },
  4: { day: "Thursday",  open: "07:00", close: "19:00" },
  5: { day: "Friday",    open: "07:00", close: "19:00" },
  6: { day: "Saturday",  open: "07:00", close: "19:00" },
};

function fmtTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const hh  = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const sfx = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${sfx}`;
}

function getLabStatus() {
  const now = new Date(
    new Date().toLocaleString("en-US", {
      timeZone: "Asia/Kolkata",
    })
  );

  const day = now.getDay();

  const currentMinutes =
    now.getHours() * 60 + now.getMinutes();

  const today = schedule[day];

  const [oh, om] = today.open.split(":").map(Number);
  const [ch, cm] = today.close.split(":").map(Number);

  const openMinutes = oh * 60 + om;
  const closeMinutes = ch * 60 + cm;

  const isOpen =
    currentMinutes >= openMinutes &&
    currentMinutes < closeMinutes;

  let backEyebrow = "";
  let backTime = "";

  if (isOpen) {
    backEyebrow = "Closes At";
    backTime = fmtTime(today.close);
  } else if (currentMinutes < openMinutes) {
    backEyebrow = "Opens Today";
    backTime = fmtTime(today.open);
  } else {
    const nextDay = schedule[(day + 1) % 7];

    backEyebrow =
      day === 6
        ? "Opens Sunday"
        : "Opens Tomorrow";

    backTime = fmtTime(nextDay.open);
  }

  return {
    isOpen,
    backEyebrow,
    backTime,
  };
}

/* ── Inline SVG wave background ── */
function WaveBg() {
  return (
    <div className="ft-wave-bg" aria-hidden="true">
      <svg
        viewBox="0 0 1440 600"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        {[80, 160, 240, 320, 400, 480].map((y, i) => (
          <path
            key={i}
            d={`M0 ${y} Q360 ${y - 28 + i * 4} 720 ${y} Q1080 ${y + 28 - i * 3} 1440 ${y}`}
            fill="none"
            stroke="rgba(37,181,233,1)"
            strokeWidth="1"
            opacity={0.6 - i * 0.08}
          />
        ))}
        {[120, 200, 280, 360, 440, 520].map((y, i) => (
          <path
            key={`b${i}`}
            d={`M0 ${y} Q360 ${y + 20 - i * 3} 720 ${y} Q1080 ${y - 20 + i * 2} 1440 ${y}`}
            fill="none"
            stroke="rgba(10,77,156,1)"
            strokeWidth="0.8"
            opacity={0.5 - i * 0.06}
          />
        ))}
        {[0, 200, 400, 600, 800, 1000, 1200].map((x, i) => (
          <line
            key={`d${i}`}
            x1={x} y1="0" x2={x + 120} y2="600"
            stroke="rgba(37,181,233,0.4)"
            strokeWidth="0.5"
            opacity="0.3"
          />
        ))}
      </svg>
    </div>
  );
}

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(getLabStatus());

  useEffect(() => {
    const id = setInterval(() => setStatus(getLabStatus()), 60000);
    return () => clearInterval(id);
  }, []);

  /* Computed once — determines whether the Email icon opens the
     native mail app (mobile) or Gmail compose in a new tab (desktop) */
  const isMobile = useMemo(() => isMobileDevice(), []);

  const emailHref = isMobile
    ? `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`
    : `https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL_ADDRESS}&su=${encodeURIComponent(
        EMAIL_SUBJECT
      )}`;

  const socials = [
    {
      icon: "fa-brands fa-instagram",
      href: "https://www.instagram.com/tanbio_r_and_d_solution?igsh=bDZjMTY2bXkycGtz&igsi=bDZjMTY2bXkycGtz",
      label: "Instagram",
      color: "#E1306C",
      target: "_blank",
    },
    {
      icon: "fa-brands fa-facebook-f",
      href: "https://www.facebook.com/share/1BjZ3V7yb4/?mibextid=wwXIfr",
      label: "Facebook",
      color: "#1877F2",
      target: "_blank",
    },
    {
      icon: "fa-brands fa-whatsapp",
      href: buildWhatsAppUrl(WHATSAPP_MESSAGE),
      label: "WhatsApp",
      color: "#25D366",
      target: "_blank",
    },
    {
      icon: "fa-solid fa-envelope",
      href: emailHref,
      label: "Email",
      color: "#25B5E9",
      // mailto: stays in the same tab (native app takes over);
      // the desktop Gmail-compose fallback opens a fresh tab
      target: isMobile ? "_self" : "_blank",
    },
  ];

  function handleNav(route, scrollId) {
    if (location.pathname === route) {
      scrollId
        ? scrollToId(scrollId)
        : window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(route);
      setTimeout(() => {
        scrollId
          ? scrollToId(scrollId)
          : window.scrollTo({ top: 0, behavior: "smooth" });
      }, 120);
    }
  }

  return (
    <footer className="ft-footer">

      <WaveBg />

      <div className="ft-body">
        <div className="ft-container">

          {/* ── COL 1: Brand + Status Badge ── */}
          <div className="ft-col ft-col--brand">
            <button
              className="ft-logo-btn"
              onClick={() => handleNav("/", null)}
              aria-label="Home"
            >
              <img src={Logo} alt="TANBIO Biomed Genomics Laboratory" loading="lazy" className="ft-logo" />
            </button>

            <p className="ft-tagline">
              TANBIO Biomed Genomics Laboratory delivers accurate, reliable and
              affordable diagnostic services with cutting-edge technology and the
              highest standards of ethical practice.
            </p>

            {/*
              STATUS BADGE — NeuLancers pill style, bottom-to-top flip only.

              CSS animation rotates the inner from 0 → -180 → -360 (one direction).
              At 0deg  : front face is visible (dot + Open/Closed).
              At -180deg: back face is visible (timing info).
              At -360deg: same as 0deg — seamless loop.

              Back face CSS:  transform: rotateX(-180deg) rotateY(180deg)
              This places it at the -180deg position AND un-mirrors the horizontal
              flip that rotateX alone would cause, so text reads left-to-right.
            */}
            <div
  className="ft-status-badge"
  aria-label={
    status.isOpen
      ? `Open Now. ${status.backEyebrow} ${status.backTime}`
      : `Closed. ${status.backEyebrow} ${status.backTime}`
  }
>
  <div className="ft-status-inner">

    {/* FRONT */}

    <div
      className={`ft-badge-face ft-badge-front ${
        !status.isOpen ? "ft-badge-front--closed" : ""
      }`}
    >
      <span
        className={`ft-open-dot ${
          !status.isOpen ? "ft-open-dot--closed" : ""
        }`}
      />

      <span
        className={`ft-badge-label ${
          !status.isOpen ? "ft-badge-label--closed" : ""
        }`}
      >
        {status.isOpen ? "Open Now" : "Closed"}
      </span>
    </div>

    {/* BACK */}

    <div className="ft-badge-face ft-badge-back">
      <i className="fa-regular fa-clock ft-badge-back-icon" />

      <div className="ft-badge-back-text">
        <span className="ft-badge-back-eyebrow">
          {status.backEyebrow}
        </span>

        <span className="ft-badge-back-time">
          {status.backTime}
        </span>
      </div>
    </div>

  </div>
</div>
          </div>

          {/* ── COL 2: Quick Links ── */}
          <div className="ft-col">
            <h4 className="ft-col-title">Quick Links</h4>
            <ul className="ft-links">
              {quickLinks.map((lk) => (
                <li key={lk.label}>
                  <button
                    className="ft-link-btn"
                    onClick={() => handleNav(lk.route, lk.scrollId)}
                  >
                    <i className="fa-solid fa-chevron-right ft-chevron"></i>
                    {lk.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ── COL 3: Find Us ── */}
          <div className="ft-col">
            <h4 className="ft-col-title">Find Us</h4>

            <ul className="ft-contact-list">
              <li>
                <i className="fa-solid fa-location-dot ft-contact-icon"></i>
                <a
                  href="https://www.google.com/maps?q=10.730744,79.631105"
                  target="_blank"
                  rel="noreferrer"
                  className="ft-contact-link"
                >
                  8/45 A, Pillaiyar Kovil Street,<br />
                  Naranamangalam, Mangudi,<br />
                  Thiruvarur – 610103
                </a>
              </li>
              <li>
                <i className="fa-solid fa-phone ft-contact-icon"></i>
                <a href="tel:+917358798182" className="ft-contact-link">+91 73587 98182</a>
              </li>
            </ul>

            <div className="ft-socials">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  className="ft-social-icon"
                  aria-label={s.label}
                  target={s.target}
                  rel="noreferrer"
                  style={{ "--sc": s.color }}
                >
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>

      <div className="ft-divider" />

      <div className="ft-bottom">
        <div className="ft-bottom-inner">
          <p className="ft-copy">
            © {new Date().getFullYear()} TANBIO Biomed Genomics Laboratory. All Rights Reserved.
          </p>

          <div className="ft-neulancers">
            <span className="ft-powered">Powered by</span>
            <a
              href="https://neulancers.com"
              target="_blank"
              rel="noreferrer"
              className="ft-nl-badge"
              aria-label="NeuLancers"
            >
              <span className="ft-nl-shimmer"></span>
              <i className="fa-solid fa-bolt ft-nl-icon"></i>
              <span className="ft-nl-name">NeuLancers</span>
            </a>
          </div>
        </div>
      </div>

    </footer>
  );
}